import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import { z } from 'zod';
import { ButtonLauncher } from '../components/ButtonLauncher';
import { ChatWidget } from '../components/ChatWidget';
import { InitPayloadSchema, sdkBridge, WidgetOpenedPayloadSchema } from '../sdk-bridge';

// Define search params schema
const searchSchema = z.object({
  organizationId: z.string().min(1),
  agentId: z.string().min(1),
  mode: z.enum(['button', 'chat']).catch('chat')
});

export const Route = createFileRoute('/')({
  validateSearch: searchSchema,
  component: Index
});

function Index() {
  const searchParams = Route.useSearch();
  const navigate = useNavigate();
  const [isReady, setIsReady] = useState(false);
  const [metadata, setMetadata] = useState<Record<string, unknown> | undefined>(() =>
    sdkBridge.isEmbedded() ? undefined : { debug: true }
  );
  const [secureMetadata, setSecureMetadata] = useState<Record<string, unknown> | undefined>();
  const [defaultView, setDefaultView] = useState<'chat' | 'list' | undefined>();
  const [safeAreaInsets, setSafeAreaInsets] = useState<
    | {
        top: number;
        bottom: number;
        left: number;
        right: number;
      }
    | undefined
  >();
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [useCustomParent, setUseCustomParent] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [agentVariables, setAgentVariables] = useState<Record<string, unknown>>({});
  const [isWidgetInitialized, setIsWidgetInitialized] = useState(false);
  const [resetChatSignal, setResetChatSignal] = useState(0);

  const handleSetDarkMode = useCallback((payload: unknown) => {
    if (typeof payload === 'boolean') {
      setDarkMode(payload);
    } else if (payload && typeof payload === 'object' && 'darkMode' in payload) {
      const data = payload as { darkMode?: boolean };
      if (typeof data.darkMode === 'boolean') {
        setDarkMode(data.darkMode);
      }
    }
  }, []);

  const handleWidgetOpened = useCallback((payload: unknown) => {
    const parseResult = WidgetOpenedPayloadSchema.safeParse(payload);
    if (!parseResult.success) {
      console.error('[Widget] Invalid STRADA_WIDGET_OPENED payload from SDK:', parseResult.error);
      return;
    }
    const data = parseResult.data;

    setIsWidgetInitialized(true);
    setAgentVariables(data.agentVariables);

    if (data.metadata !== undefined) {
      setMetadata(data.metadata);
    }

    if (data.secureMetadata !== undefined) {
      setSecureMetadata(data.secureMetadata);
    }

    if (data.safeAreaInsets !== undefined) {
      setSafeAreaInsets(data.safeAreaInsets);
    }
  }, []);

  useEffect(() => {
    const handleInit = (payload: unknown) => {
      const parseResult = InitPayloadSchema.safeParse(payload);
      if (!parseResult.success) {
        console.error('[Widget] Invalid STRADA_INIT payload from SDK:', parseResult.error);
        return;
      }
      const initData = parseResult.data;

      if (initData.metadata) {
        setMetadata(initData.metadata);
      }
      if (initData.secureMetadata) {
        setSecureMetadata(initData.secureMetadata);
      }
      if (initData.defaultView) {
        setDefaultView(initData.defaultView);
      }
      if (initData.safeAreaInsets) {
        setSafeAreaInsets(initData.safeAreaInsets);
      }
      if (typeof initData.keyboardHeight === 'number') {
        setKeyboardHeight(Math.max(0, Math.ceil(initData.keyboardHeight)));
      } else {
        setKeyboardHeight(0);
      }
      if (typeof initData.useCustomParent === 'boolean') {
        setUseCustomParent(initData.useCustomParent);
      }
      if (typeof initData.darkMode === 'boolean') {
        setDarkMode(initData.darkMode);
      }

      setAgentVariables(initData.agentVariables);

      if (!searchParams.organizationId || !searchParams.agentId) {
        navigate({
          to: '/',
          search: {
            organizationId: initData.organizationId,
            agentId: initData.agentId,
            mode: searchParams.mode
          },
          replace: true
        });
      }

      setIsReady(true);
    };

    const handleKeyboardHeight = (payload: unknown) => {
      const data = payload as { height?: unknown; phase?: unknown };
      const height = Number(data?.height ?? 0);
      if (Number.isFinite(height)) {
        setKeyboardHeight(Math.max(0, Math.ceil(height)));
      }
    };

    const handleNewChat = (payload: unknown) => {
      const parseResult = WidgetOpenedPayloadSchema.safeParse(payload);
      if (!parseResult.success) return;
      const data = parseResult.data;

      setAgentVariables(data.agentVariables);
      if (data.metadata !== undefined) setMetadata(data.metadata);
      if (data.secureMetadata !== undefined) setSecureMetadata(data.secureMetadata);

      setResetChatSignal((s) => s + 1);
    };

    sdkBridge.on('STRADA_INIT', handleInit);
    sdkBridge.on('STRADA_WIDGET_OPENED', handleWidgetOpened);
    sdkBridge.on('STRADA_KEYBOARD_HEIGHT', handleKeyboardHeight);
    sdkBridge.on('STRADA_SET_DARK_MODE', handleSetDarkMode);
    sdkBridge.on('STRADA_NEW_CHAT', handleNewChat);

    let embedCheckTimer: number | undefined;

    if (sdkBridge.isEmbedded()) {
      sdkBridge.sendReady();
      if (searchParams.mode === 'button' && searchParams.organizationId && searchParams.agentId) {
        setIsReady(true);
      }
    } else {
      embedCheckTimer = window.setInterval(() => {
        if (sdkBridge.isEmbedded()) {
          window.clearInterval(embedCheckTimer);
          sdkBridge.sendReady();
        }
      }, 100);

      setAgentVariables({});
      setIsReady(true);
    }

    return () => {
      sdkBridge.off('STRADA_INIT', handleInit);
      sdkBridge.off('STRADA_WIDGET_OPENED', handleWidgetOpened);
      sdkBridge.off('STRADA_KEYBOARD_HEIGHT', handleKeyboardHeight);
      sdkBridge.off('STRADA_SET_DARK_MODE', handleSetDarkMode);
      sdkBridge.off('STRADA_NEW_CHAT', handleNewChat);
      if (embedCheckTimer) {
        window.clearInterval(embedCheckTimer);
      }
    };
  }, [
    navigate,
    searchParams.agentId,
    searchParams.mode,
    searchParams.organizationId,
    handleSetDarkMode,
    handleWidgetOpened
  ]);

  // Apply dark mode class to document element
  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
  }, [darkMode]);

  // Show loading state while waiting for initialization
  if (!isReady) {
    return null;
  }

  // Show error if required params are missing
  if (!searchParams.organizationId || !searchParams.agentId) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Strada Widget</h1>
          <p className="text-red-600 mb-4">Missing required parameters</p>
          <div className="bg-gray-100 rounded p-4 text-left text-sm">
            <p className="font-semibold mb-2">Required URL Parameters:</p>
            <p className="text-gray-700">
              <span className="font-medium">organizationId:</span>{' '}
              {searchParams.organizationId || '❌ Missing'}
            </p>
            <p className="text-gray-700">
              <span className="font-medium">agentId:</span> {searchParams.agentId || '❌ Missing'}
            </p>
          </div>
          <p className="text-xs text-gray-500 mt-6">
            Example: ?organizationId=123&agentId=456
            <br />
            Or embed using the Strada SDK
          </p>
        </div>
      </div>
    );
  }

  if (searchParams.mode === 'button') {
    return <ButtonLauncher organizationId={searchParams.organizationId} agentId={searchParams.agentId} />;
  }

  return (
    <div className="app-shell">
      <ChatWidget
        organizationId={searchParams.organizationId}
        agentId={searchParams.agentId}
        agentVariables={agentVariables}
        isEmbedded={sdkBridge.isEmbedded()}
        isWidgetInitialized={isWidgetInitialized}
        metadata={metadata}
        secureMetadata={secureMetadata}
        defaultView={defaultView}
        safeAreaInsets={safeAreaInsets}
        keyboardHeight={keyboardHeight}
        useCustomParent={useCustomParent}
        darkMode={darkMode}
        resetChatSignal={resetChatSignal}
      />
    </div>
  );
}
